import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle, ExternalLink, Instagram, Linkedin, Twitter, Youtube, Music } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformIcons = {
  instagram: Instagram,
  linkedin: Linkedin,
  twitter: Twitter,
  youtube: Youtube,
  music: Music, // TikTok
};

const PublicFollowPage = () => {
  const { code } = useParams();
  const [plaque, setPlaque] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    fetchPlaque();
  }, [code]);

  const fetchPlaque = async () => {
    try {
      const response = await axios.get(`${API}/f/${code}`);
      setPlaque(response.data);
      
      // Auto-redirect after a short delay
      setTimeout(() => {
        handleRedirect(response.data.redirect_url);
      }, 2000);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('This follow plaque was not found.');
      } else if (err.response?.status === 400) {
        setError(err.response?.data?.detail || 'This plaque has not been activated yet.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRedirect = (url) => {
    setRedirecting(true);
    window.location.href = url;
  };

  const getPlatformIcon = (iconName) => {
    const iconKey = iconName?.toLowerCase();
    return platformIcons[iconKey] || Instagram;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <Card className="max-w-md w-full shadow-layered border-0">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="font-dashboard-heading text-xl font-bold mb-2">Oops!</h1>
            <p className="text-[var(--color-text-secondary)]">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const PlatformIcon = getPlatformIcon(plaque?.platform_icon);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-50 p-6">
      <Card className="max-w-md w-full shadow-layered border-0">
        <CardContent className="pt-8 pb-8 text-center">
          {/* Header */}
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <PlatformIcon className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="font-dashboard-heading text-2xl font-bold mb-1" data-testid="follow-business-name">
            {plaque.business_name}
          </h1>
          <p className="text-[var(--color-text-muted)] mb-6">
            Follow us on {plaque.platform_name}
          </p>
          
          {redirecting ? (
            <div className="animate-fade-in-up">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-[var(--color-text-secondary)]">
                Redirecting to {plaque.platform_name}...
              </p>
            </div>
          ) : (
            <div className="animate-fade-in-up">
              <p className="text-[var(--color-text-secondary)] mb-6">
                You'll be redirected automatically, or click below:
              </p>
              
              <Button 
                onClick={() => handleRedirect(plaque.redirect_url)}
                className="w-full h-12 rounded-full btn-lift text-base"
                data-testid="follow-redirect-btn"
              >
                <PlatformIcon className="w-5 h-5 mr-2" />
                Follow on {plaque.platform_name}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicFollowPage;
